import type { ThriveRepositories } from './interfaces/repositories';

/**
 * **A parent's delete, as a boundary every writer in the app can see** (P6R13-1/P6R14-1..4, BL-003,
 * PS6-3).
 *
 * `family-erasure.ts` makes the bytes physically go away and checks that they did. That is a claim
 * about the disk at one instant, and without this file the app could falsify it a moment later: a
 * sitting that was already under way holds repositories, queues and caches that all predate the
 * delete, and anything it writes next puts the family's record back under the same key. A parent had
 * been told, in as many words, that everything was deleted *and checked*.
 *
 * Round 13 put that boundary in the component that happened to notice it first. Round 14 moves it
 * where it belongs — the storage layer — after three findings that were all the same mistake seen
 * from different angles: a guard on one record while the sitting wrote four others; a guard asked
 * before a queue rather than after it; and a watcher installed by a passive effect, so a delete that
 * arrived a tick too early was dropped. The rules below are therefore deliberately few:
 *
 *  1. **One epoch, realm-wide, installed at storage bootstrap.** Not a component's to own, not a
 *     component's to miss. Everything born before the current epoch is stale.
 *  2. **One scoping function.** A sitting is handed repositories that refuse *every* governed
 *     writer, not one hand-picked record.
 *  3. **The question is asked at the commit point.** After every wait, immediately before the bytes
 *     are handed over — because "was this allowed when it started?" is not the question a delete
 *     asks.
 *  4. **Writers and deletes take a reader/writer lock.** A delete cannot begin while a governed
 *     write is in flight, so `complete: true` is true when it is said. In-realm the lock is this
 *     module's own; across tabs it is the Web Locks API where the browser has one.
 *
 * **In-memory only.** No new `thrive.v1.*` key, nothing persisted: an epoch that outlived the tab
 * would be a fact about a browser session written into a family's storage, which is precisely the
 * kind of thing this product does not keep. The Web Locks *name* is a lock name, not a storage key.
 */

/** The current epoch. Starts at zero; every delete moves it, and nothing else does. */
let epoch = 0;

type ErasureListener = (epoch: number) => void;

const listeners = new Set<ErasureListener>();

/** Installed once per realm, from storage bootstrap. */
let crossContextWatcherInstalled = false;

/** The namespace this app owns. A removal outside it is somebody else's business. */
const NAMESPACE_PREFIX = 'thrive.v1.';

/**
 * The cross-tab lock every governed write and every delete passes through.
 *
 * A name, not a storage key: `navigator.locks` has its own namespace, holds nothing, and leaves
 * nothing behind. Flagged for the storage review anyway, because it is a new named resource in the
 * app's namespace even though no byte of it is ever written.
 */
export const GOVERNED_WRITE_LOCK = 'thrive.v1.governed-writes';

export function currentErasureEpoch(): number {
  return epoch;
}

/**
 * Records that a delete has happened, and tells everybody who is still holding pre-delete state.
 *
 * Listeners are notified from a copy of the set: a listener that unsubscribes itself while being
 * notified must not change the collection being walked.
 */
export function noteErasure(): number {
  epoch += 1;
  for (const listener of [...listeners]) listener(epoch);
  return epoch;
}

/** Subscribes to the boundary. Returns an unsubscribe function. */
export function subscribeToErasure(listener: ErasureListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Whether a write born under `bornEpoch` is still allowed to land.
 *
 * One rule, in one place, so no two callers can come to different conclusions about the same write.
 */
export function isPreErasure(bornEpoch: number): boolean {
  return bornEpoch !== epoch;
}

/**
 * Listens for another context destroying one of our governed records (P6R14-3).
 *
 * Called from the storage layer's own construction, synchronously, so the boundary is alive before
 * anything renders. `window.storage` fires in *other* documents of the same origin, which is what
 * makes it the right signal and also what makes it invisible from a single realm — the two-page e2e
 * is what exercises it.
 *
 * **Fail-closed on purpose**: a removal of a key in our namespace, or a whole-store `clear()`
 * (`key === null`), is treated as a delete. The cost of being wrong is a sitting that stops
 * checkpointing; the cost of the other mistake is a deleted family record coming back.
 */
export function installCrossContextWatcher(): void {
  if (crossContextWatcherInstalled) return;
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
  crossContextWatcherInstalled = true;
  window.addEventListener('storage', (event: StorageEvent) => {
    const erasedOurs =
      event.key === null || (event.key.startsWith(NAMESPACE_PREFIX) && event.newValue === null);
    if (erasedOurs) noteErasure();
  });
}

/**
 * A reader/writer lock for this realm, always present, plus the browser's own across tabs.
 *
 * Two reasons it is written out rather than left to `navigator.locks`. The first is correctness
 * where there is no such API — a test environment, an older browser — because the *in-realm*
 * ordering this provides is what makes a delete wait for a write that is already in flight, and
 * that guarantee must not be a browser feature. The second is that the residual is then honest and
 * small: with `navigator.locks` the guarantee holds across tabs too, and without it, it holds
 * within the tab and the cross-tab window is the recorded limit stated in `family-erasure`'s claim.
 *
 * Deliberately not fair or re-entrant. It is held for the duration of one governed write or one
 * delete, both of which are short and neither of which takes it twice.
 */
let sharedHolders = 0;
let exclusiveHeld = false;
const waiting: { mode: 'shared' | 'exclusive'; admit: () => void }[] = [];

/**
 * Hands the lock to whoever may have it, **taking ownership as it does so** (P6R15-3).
 *
 * The first version resolved every eligible waiter and left each resumed continuation to claim
 * ownership afterwards — so one pump could admit two queued deletes, each of which then set
 * `exclusiveHeld` and believed itself alone. A lock that says "exclusive" and is not is a machinery
 * defect whether or not today's UI can produce two deletes.
 *
 * So ownership is assigned **here**, before any continuation runs: one exclusive waiter, or a run of
 * shared ones, and the queue is left holding everybody who did not get in.
 */
function pump(): void {
  while (waiting.length > 0) {
    const next = waiting[0];
    if (next === undefined) return;
    if (next.mode === 'exclusive') {
      if (exclusiveHeld || sharedHolders > 0) return;
      exclusiveHeld = true;
    } else {
      if (exclusiveHeld) return;
      sharedHolders += 1;
    }
    waiting.shift();
    next.admit();
  }
}

async function acquireRealmLock(mode: 'shared' | 'exclusive'): Promise<() => void> {
  const free = mode === 'shared' ? !exclusiveHeld : !exclusiveHeld && sharedHolders === 0;

  if (free && waiting.length === 0) {
    // Taken here for the same reason the pump takes it: ownership and admission are one step.
    if (mode === 'shared') sharedHolders += 1;
    else exclusiveHeld = true;
  } else {
    await new Promise<void>((resolve) => {
      waiting.push({ mode, admit: resolve });
    });
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (mode === 'shared') sharedHolders -= 1;
    else exclusiveHeld = false;
    pump();
  };
}

interface LockManagerLike {
  request(
    name: string,
    options: { mode: 'shared' | 'exclusive' },
    callback: () => Promise<unknown>,
  ): Promise<unknown>;
}

function lockManager(): LockManagerLike | null {
  const locks = (globalThis.navigator as { locks?: LockManagerLike } | undefined)?.locks;
  return typeof locks?.request === 'function' ? locks : null;
}

/** Runs `work` holding the boundary lock in `mode`, in this realm and — where possible — across tabs. */
async function holdingLock<T>(mode: 'shared' | 'exclusive', work: () => Promise<T>): Promise<T> {
  const releaseRealm = await acquireRealmLock(mode);
  try {
    const locks = lockManager();
    if (locks === null) return await work();
    let answer: T;
    await locks.request(GOVERNED_WRITE_LOCK, { mode }, async () => {
      answer = await work();
    });
    // Assigned by the callback above, which `request` awaits before resolving.
    return answer!;
  } finally {
    releaseRealm();
  }
}

/**
 * Runs one governed write, refusing it if the sitting it belongs to predates a delete.
 *
 * The epoch is read **inside the lock, immediately before the bytes are handed over** (P6R14-2).
 * Asking earlier answers a different question: a write admitted before a delete began would sail
 * through every later wait and land on the far side of a `complete: true` that was true when it was
 * said.
 *
 * Refusal resolves rather than rejects: this is not a storage failure, there is nothing for a
 * mission to recover from, and the record it wanted to write is one the family has asked us to
 * forget. Forgetting it is the correct outcome, not a degraded one.
 */
export function runGovernedWrite(
  epochOf: () => number,
  write: () => Promise<void>,
  precondition?: () => boolean,
): Promise<void> {
  const bornEpoch = epochOf();
  return holdingLock('shared', async () => {
    /**
     * Two questions, and only the second one is load-bearing (P6R15-1).
     *
     * The epoch is **fail-fast only** now, and its comment says so: it is an in-memory fact, and a
     * lock grant and a `storage` event come from different task sources with no ordering between
     * them — so the other tab can be granted this lock *before* its event arrives, with its epoch
     * still saying "current". No amount of ordering fixes that, because the thing being ordered is
     * not the thing that is wrong.
     *
     * The condition that cannot be stale is one **read from the store, here, under the lock**: does
     * the record this write belongs to still exist? An erased family has no anchor, so a write
     * predicated on one refuses however the task sources interleaved. That is D-093's rule — a
     * condition must be derived, not stored — applied to deletion.
     */
    if (isPreErasure(bornEpoch)) return;
    if (precondition !== undefined && !precondition()) return;
    await write();
  });
}

/**
 * Runs a delete, excluding every governed write for its whole duration — the erase *and* its
 * read-back — and moves the boundary afterwards.
 *
 * Exclusive on both sides of the lock, so a write already in flight finishes before the delete
 * starts (and is then erased by it), and a write that arrives during the delete waits until the
 * boundary has moved and is refused. That is what makes `complete: true` a claim about the disk at
 * a moment nothing else could be changing it.
 */
export async function runErasure<T>(erase: () => Promise<T>): Promise<T> {
  const result = await holdingLock('exclusive', erase);
  noteErasure();
  return result;
}

/**
 * **The anchor records a sitting's writes are predicated on** (P6R15-1).
 *
 * The map, stated once and deliberately small:
 *
 * | write | anchor it requires to still exist |
 * | --- | --- |
 * | `skills.recordEvidence` | the **generation** of every child the rows are about |
 * | `progress.saveGameProgress` | the **generation** of the child whose progress it is |
 * | `missionProgress.saveProgress` | the session row the snapshot names |
 *
 * **A child anchor is a generation, not an id, and it is CARRIED not re-read** (P6R16-2/P6R17-1,
 * D-056(6)). The sitting is identified by who was **chosen**; a lookup at boot answers only "who
 * holds this id now", which is a different question and can name a replacement child. A named seat
 * with no carried generation **fails closed** — there is no id-only fallback in this path, and no
 * caller is exempted from the rule.
 *
 * The original statement of the rule: Profile ids are caller-supplied
 * and `saveChild` upserts on them, so an id alone cannot tell child A from a child B created under
 * the same id after A was deleted — and a family who deletes everything and types the same name
 * again produces exactly that. The profile's own `createdAt` is the generation, it is already
 * stored, and the sitting captures it at boot; the condition compares **both** against the profile
 * that is in the store now. Getting this wrong does not lose rows, it *misattributes* them: one
 * child's academic record driving another child's adaptation and appearing in their parent summary.
 *
 * **`sessions.completeSession` is deliberately UNANCHORED** (D-124(4), reaffirmed by P6R16-4). The
 * session history is bounded, so its row can be legitimately absent by the time a mission ends, and
 * a refusal there would cost a family their summary for no gain. It is guarded by the lock and the
 * sitting's fail-fast epoch, and it declines to write **empty envelopes** for a session that is no
 * longer there — a delete should not leave residue, even empty residue.
 *
 * Everything else is either **anchor-establishing** (`sessions.startSession`,
 * `launchIdentity.save`, `profiles.saveChild`, `profiles.saveFamily` — the writes a genuinely new
 * post-erasure sitting makes to bring its own anchors into being) or **destructive only**
 * (`clearProgress`, `launchIdentity.clear`, `deleteChild`), and neither can resurrect a family's
 * data. They take the lock and stop there.
 *
 * The point of anchoring to *records* rather than to a flag: a delete removes them, so the answer is
 * derived from the store at the moment the write would land — and no interleaving of lock grants and
 * `storage` events can make a deleted record look present.
 */
export interface GovernedWriteAnchors {
  /** Whether this session's own row is still in the store. */
  sessionExists(sessionId: string): boolean;
  /** Whether this child still has a profile. */
  childExists(profileId: string): boolean;
  /**
   * Whether the profile stored under this id is still **the same child** (P6R16-2).
   *
   * `createdAt` is the generation (D-056(6)). A profile that is absent, or present under a different
   * generation, is not the child this sitting is about.
   */
  childGenerationMatches(profileId: string, createdAt: string): boolean;
}

/** What a sitting knows about the children it is for: id → the generation captured at boot. */
export type SittingGenerations = ReadonlyMap<string, string>;

/**
 * The **general** guarded layer: every governed write in the app takes the shared lock and, where it
 * has one, checks its anchor (P6R15-2).
 *
 * Applied at construction, so the bundle the shell is handed *is* the guarded bundle and there is no
 * raw one to hand out by accident. Round 14 wrapped the sitting and left the parent zone writing
 * `saveFamily` straight through — the one-path rule with a page-shaped hole in it.
 *
 * The sitting's own scope (`scopeRepositoriesToSitting`) sits on top of this and adds nothing but the
 * fail-fast epoch.
 */
/**
 * The anchor condition the **next** guarded call should carry, handed over synchronously.
 *
 * The sitting layer knows which anchor a write depends on; the guarded layer owns the lock. Stacking
 * one on the other would acquire a deliberately non-re-entrant lock twice — a deadlock, not a
 * defence — and building the sitting a *sibling* bundle would bypass the very object the app was
 * handed (and every test spy on it). So the condition is passed down instead: set immediately before
 * the delegating call, consumed at the top of it, evaluated inside the lock.
 *
 * Safe because the handoff is synchronous: nothing can run between the set and the consume, on a
 * single-threaded runtime, and the consumer clears it whether or not it uses it.
 */
let pendingPrecondition: (() => boolean) | null = null;

/** The anchors a guarded bundle reads, so the sitting built on it can name the same records. */
const guardedAnchors = new WeakMap<ThriveRepositories, GovernedWriteAnchors>();

function takePendingPrecondition(): (() => boolean) | undefined {
  const pending = pendingPrecondition;
  pendingPrecondition = null;
  return pending ?? undefined;
}

export function withGovernedWrites(
  repositories: ThriveRepositories,
  anchors: GovernedWriteAnchors,
): ThriveRepositories {
  const always = (): number => currentErasureEpoch();
  /**
   * Every governed write: the shared lock, the fail-fast epoch, and — where the caller handed one
   * down — the anchor condition, read from the store inside the lock at the commit point.
   */
  const guard = (write: () => Promise<void>): Promise<void> =>
    runGovernedWrite(always, write, takePendingPrecondition());

  const guarded: ThriveRepositories = {
    ...repositories,
    /**
     * **Profile writes are deliberately NOT on the lock** (P6R15-2, and a regression this round
     * caught red-handed).
     *
     * Putting them there cost a family their settings: the parent zone's checkbox writes and a
     * grown-up navigates immediately, and with `navigator.locks` in the path the write had not landed
     * before the page went away. That is a certain, ordinary harm — a parent turns the sound off and
     * it comes back on — traded against a narrow interleaving window.
     *
     * The window is closed from the other side instead, and better: the delete's read-back is taken
     * **after all of its work**, so a profile write that lands mid-erase is *seen* and reported. The
     * certification stops being false, which is what the finding is actually about; it does not
     * pretend the write was prevented.
     */
    profiles: repositories.profiles,
    progress: {
      ...repositories.progress,
      saveGameProgress: (playerId, gameId, progress) =>
        guard(() => repositories.progress.saveGameProgress(playerId, gameId, progress)),
    },
    sessions: {
      ...repositories.sessions,
      startSession: (session) => guard(() => repositories.sessions.startSession(session)),
      completeSession: (sessionId, summary) =>
        guard(() => repositories.sessions.completeSession(sessionId, summary)),
    },
    skills: {
      ...repositories.skills,
      recordEvidence: (evidence) => guard(() => repositories.skills.recordEvidence(evidence)),
    },
    missionProgress: {
      ...repositories.missionProgress,
      saveProgress: (snapshot) => guard(() => repositories.missionProgress.saveProgress(snapshot)),
      clearProgress: () => guard(() => repositories.missionProgress.clearProgress()),
    },
    launchIdentity: {
      ...repositories.launchIdentity,
      save: (record) => guard(() => repositories.launchIdentity.save(record)),
      clear: () => guard(() => repositories.launchIdentity.clear()),
    },
  };

  guardedAnchors.set(guarded, anchors);
  return guarded;
}

/**
 * Carries a guarded bundle's origin onto the object a caller actually hands out.
 *
 * `createLocalRepositories` returns the guarded bundle **plus** its own members (`backend`,
 * `eraseEverything`, …), which is a different object — so without this the sitting scope would look up
 * an origin it could not find and quietly fall back to lock-only, losing the anchor conditions
 * exactly where they matter most. A silent fallback that costs a guarantee is worth one explicit
 * line.
 */
export function inheritGovernedOrigin<T extends ThriveRepositories>(
  handed: T,
  guarded: ThriveRepositories,
): T {
  const anchors = guardedAnchors.get(guarded);
  if (anchors !== undefined) guardedAnchors.set(handed, anchors);
  return handed;
}

/**
 * The repositories a **sitting** writes through: every governed writer, guarded by one rule
 * (P6R14-1).
 *
 * Round 13 guarded the mission snapshot, which is one of the five records a sitting produces. A
 * named child's stale sitting went on writing skill evidence — the closest thing this product keeps
 * to a record of a child — along with their progression and the session row, minutes after their
 * family had been told all of it was deleted. Guarding a hand-picked record is how that happens; the
 * unit that has to be guarded is the *bundle*.
 *
 * Reads are untouched on purpose. A stale sitting reading its own state hurts nobody, and refusing
 * reads would break the screen a child is still looking at for no gain (D-122(4): it stops
 * persisting, it does not stop).
 *
 * `epochOf` is a function rather than a number because a sitting is not a component: "Play again"
 * starts a genuinely new one without remounting anything, and that new run is entitled to the
 * boundary as it stands *then* (P6R14-4).
 */
export function scopeRepositoriesToSitting(
  repositories: ThriveRepositories,
  epochOf: () => number,
  generationsOf?: () => SittingGenerations,
): ThriveRepositories {
  const anchors = guardedAnchors.get(repositories);

  /**
   * The child condition, in one place so evidence and progression cannot answer it differently.
   *
   * A child this sitting captured a generation for must still be **that** child; one it did not
   * capture (a guest slot, or a caller that supplied no generations at all) falls back to "does this
   * profile exist", which is what the anchor could say before generations existed.
   */
  const childIsStillTheSameChild = (profileId: string): boolean => {
    if (anchors === undefined) return true;
    const generation = generationsOf?.().get(profileId);
    /**
     * **No id-only fallback, ever, in a sitting** (P6R17-1).
     *
     * An id cannot tell a child from a child created under the same id after the first was deleted —
     * which is precisely the case this anchor exists for. A sitting that cannot say *which
     * generation* it is for therefore refuses: losing rows is recoverable, and attaching one child's
     * learning to another is not. Nothing in the shipped sitting path lacks a generation; a caller
     * that genuinely had no generation facility would have to be listed in the anchor map with its
     * reason, and there is none.
     */
    return generation === undefined ? false : anchors.childGenerationMatches(profileId, generation);
  };

  /**
   * **The sitting's half: fail fast, and name the anchor** (P6R15-1).
   *
   * It delegates to the bundle it was given — the guarded one — so there is exactly one lock
   * acquisition per write and the object the app was handed stays the object that does the writing.
   * The epoch here is a courtesy that saves queueing work a sitting already knows is doomed; the
   * guarantee is the condition below, read from the store under the lock.
   *
   * A bundle with no anchors registered (a hand-built one, in a test) keeps the lock and the epoch
   * and simply has no anchor to check — the honest degradation, and it never silently *passes* a
   * write that a real anchor would refuse, because there is no erasure in such a bundle's world.
   */
  const guard = (write: () => Promise<void>, precondition?: () => boolean): Promise<void> => {
    if (isPreErasure(epochOf())) return Promise.resolve();
    if (precondition !== undefined) pendingPrecondition = precondition;
    return write();
  };

  return {
    ...repositories,
    /**
     * Every writer a sitting has, not only the two with anchors.
     *
     * The guarded layer beneath cannot answer this question: it reads the epoch at call time, so for
     * it "born" and "current" are the same number and the check is vacuous by construction. Staleness
     * is a fact about *this sitting*, and only the sitting knows when it began — so the fail-fast
     * belongs here, and the guarantee for the two records a delete is most obliged to keep deleted
     * belongs to their anchors below.
     */
    progress: {
      ...repositories.progress,
      saveGameProgress: (playerId, gameId, progress) =>
        guard(
          () => repositories.progress.saveGameProgress(playerId, gameId, progress),
          // The anchor the map promised and round 15 never supplied (P6R16-1): a completion reaching
          // this path after a delete restored a deleted child's shards, missions and best badge.
          anchors === undefined ? undefined : () => childIsStillTheSameChild(playerId),
        ),
    },
    sessions: {
      ...repositories.sessions,
      startSession: (session) => guard(() => repositories.sessions.startSession(session)),
      completeSession: (sessionId, summary) =>
        guard(() => repositories.sessions.completeSession(sessionId, summary)),
    },
    launchIdentity: {
      ...repositories.launchIdentity,
      save: (record) => guard(() => repositories.launchIdentity.save(record)),
      clear: () => guard(() => repositories.launchIdentity.clear()),
    },
    skills: {
      ...repositories.skills,
      recordEvidence: (evidence) =>
        guard(
          () => repositories.skills.recordEvidence(evidence),
          anchors === undefined
            ? undefined
            : // Every child the rows are about, at the generation this sitting is for. A surviving
              // row about a deleted child is the record a delete is most obliged to keep deleted —
              // and a row *reattached* to their replacement is worse than one that survived.
              () => evidence.every((row) => childIsStillTheSameChild(row.playerId)),
        ),
    },
    missionProgress: {
      ...repositories.missionProgress,
      saveProgress: (snapshot) =>
        guard(
          () => repositories.missionProgress.saveProgress(snapshot),
          anchors === undefined ? undefined : () => anchors.sessionExists(snapshot.sessionId),
        ),
      clearProgress: () => guard(() => repositories.missionProgress.clearProgress()),
    },
  };
}

/**
 * Test-only reset, so one suite's delete cannot make the next suite's sitting stale.
 *
 * Module state shared by every bundle in a realm is exactly right for the product — one tab, one
 * boundary — and exactly wrong for a test file that builds twenty sittings in one realm.
 */
export function resetErasureBoundaryForTests(): void {
  epoch = 0;
  listeners.clear();
  sharedHolders = 0;
  exclusiveHeld = false;
  waiting.length = 0;
}
